import type {
  MarketDomainServerMessage,
  MarketStreamSubscription,
} from '@hypersense/contracts/market';
import { exhaustiveGuard } from '@hypersense/shared/typing';

export function queueMarketFeedPendingMessage(input: {
  currentPendingMessages: readonly MarketDomainServerMessage[];
  nextMessage: MarketDomainServerMessage;
  subscription: MarketStreamSubscription | null;
}) {
  if (
    !doesMessageMatchCurrentSubscription({
      message: input.nextMessage,
      subscription: input.subscription,
    })
  ) {
    return input.currentPendingMessages.slice();
  }

  if (!readIsStatsOnlySubscription(input.subscription)) {
    return [...input.currentPendingMessages, input.nextMessage];
  }

  switch (input.nextMessage.type) {
    case 'market.stats':
    case 'market.stats_snapshot':
    case 'error':
      return [input.nextMessage];
    case 'heartbeat':
    case 'market.bar':
    case 'market.bars_snapshot':
    case 'market.ladder':
    case 'market.ladder_snapshot':
    case 'market.liquidity_delta':
    case 'market.liquidity_snapshot':
    case 'market.trades_append':
    case 'market.trades_snapshot':
      return input.currentPendingMessages.slice();
    default:
      return exhaustiveGuard(input.nextMessage);
  }
}

function readIsStatsOnlySubscription(
  subscription: MarketStreamSubscription | null,
) {
  if (subscription === null) {
    return false;
  }

  return (
    subscription.resources.bars === undefined &&
    subscription.resources.liquidity === undefined &&
    subscription.resources.ladder === undefined &&
    subscription.resources.stats === true &&
    subscription.resources.trades === undefined
  );
}

function doesMessageMatchCurrentSubscription(input: {
  message: MarketDomainServerMessage;
  subscription: MarketStreamSubscription | null;
}) {
  switch (input.message.type) {
    case 'market.bar':
    case 'market.bars_snapshot':
      return (
        input.subscription?.resources.bars?.interval === input.message.interval
      );
    case 'market.ladder':
    case 'market.ladder_snapshot':
      return (
        input.subscription?.resources.ladder?.grouping ===
        input.message.grouping
      );
    case 'market.liquidity_delta':
    case 'market.liquidity_snapshot':
      return (
        input.subscription?.resources.liquidity?.windowMinutes ===
        input.message.windowMinutes
      );
    case 'market.stats':
    case 'market.stats_snapshot':
      return input.subscription?.resources.stats === true;
    case 'market.trades_append':
    case 'market.trades_snapshot':
      return input.subscription?.resources.trades !== undefined;
    case 'error':
    case 'heartbeat':
      return true;
    default:
      return exhaustiveGuard(input.message);
  }
}
